import { CANVAS_WIDTH } from "../../game/constants";
import type { Rect, UiRects } from "../../game/input";
import type { ProfileVisibleItem } from "../../game/profileVisibleItems";
import type { SoloMode } from "../../game/types";
import type { RankedTier } from "../../versus/ranked";
import type { RenderModel } from "../renderModel";
import { GAME_FONT, MENU_CONTENT_OFFSET_PX, uiCloseSize, UI_CLOSE_Y, UI_EDGE_PAD } from "../renderConstants";
import { RARITY_COLORS } from "../../economy/characters";

// ── Dependency interface ────────────────────────────────────────────

export interface ProfileBrowseDeps {
  ctx: CanvasRenderingContext2D;
  canvasHeight: number;
  nowMs: () => number;
  drawCircleCloseButton: (rect: Rect) => void;
  drawCirclePencilButton: (rect: Rect) => void;
  drawCharacterPortrait: (
    path: string, x: number, y: number, w: number, h: number, anchor?: string,
  ) => void;
  leaderboardTierColor: (tier: RankedTier) => string;
  rankVinylImages: Partial<Record<RankedTier, HTMLImageElement>>;
  soloSelectorLogoImages: Record<SoloMode, HTMLImageElement | null>;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawProfileBrowseScreen(
  deps: ProfileBrowseDeps,
  model: RenderModel,
  profileItems: ProfileVisibleItem[],
  uiRects: UiRects,
  _filter: "all" | "wallet",
): UiRects {
  const {
    ctx, canvasHeight, nowMs,
    drawCircleCloseButton, drawCirclePencilButton, drawCharacterPortrait,
    leaderboardTierColor, rankVinylImages, soloSelectorLogoImages,
  } = deps;
  const centerX = CANVAS_WIDTH / 2;
  const canvasH = canvasHeight;

  // Close button top-right
  const closeRect: Rect = { x: CANVAS_WIDTH - uiCloseSize() - UI_EDGE_PAD, y: UI_CLOSE_Y, w: uiCloseSize(), h: uiCloseSize() };
  drawCircleCloseButton(closeRect);
  uiRects.profileBack = closeRect;

  // Customise button next to close
  const pencilRect: Rect = { x: closeRect.x - uiCloseSize() - 6, y: UI_CLOSE_Y, w: uiCloseSize(), h: uiCloseSize() };
  drawCirclePencilButton(pencilRect);
  uiRects.profileEditSkin = pencilRect;

  // Current equipped character (always show equipped skin in showcase)
  const equippedItem = profileItems.find((i) => i.equipped) ?? profileItems[0];
  const charPath = equippedItem?.imagePath ?? model.equippedCharacter.imageSmallPath;

  // Character portrait — large, centered, bobbing
  const bobY = Math.sin(nowMs() / 800) * 2;
  const portraitH = canvasH * 0.667;
  const portraitW = portraitH * 0.85;
  const portraitTopY = canvasH - MENU_CONTENT_OFFSET_PX - portraitH;
  const portraitY = portraitTopY + bobY;
  const portraitBaseX = centerX - portraitW / 2;

  // Rarity glow behind character
  const rarityColor = equippedItem
    ? ((RARITY_COLORS as Record<string, string>)[equippedItem.rarity] ?? "#3a4870")
    : "#3a4870";
  if (equippedItem && equippedItem.rarity !== "COMMON") {
    const glowRad = portraitW * 0.5;
    const gcx = portraitBaseX + portraitW / 2;
    const gcy = portraitY + portraitH * 0.35;
    const grd = ctx.createRadialGradient(gcx, gcy, 0, gcx, gcy, glowRad);
    grd.addColorStop(0, rarityColor + "40");
    grd.addColorStop(1, rarityColor + "00");
    ctx.fillStyle = grd;
    ctx.fillRect(gcx - glowRad, gcy - glowRad, glowRad * 2, glowRad * 2);
  }

  drawCharacterPortrait(charPath, portraitBaseX, portraitY, portraitW, portraitH, "bottom");
  uiRects.profilePortrait = { x: portraitBaseX, y: portraitY, w: portraitW, h: portraitH };

  // ── Vinyl record with crossfade flip ──
  const rankContext = model.versusRankContext;
  const player = rankContext?.player ?? null;
  const tier: RankedTier = player?.tier ?? "Regular";
  const rankNum = player?.rank ?? null;
  const hasGames = player != null && (player.rankedWins > 0 || player.rankedLosses > 0);

  const vinylImg = rankVinylImages[tier] ?? null;
  const vinylSize = 162;
  const vinylCenterY = portraitTopY - vinylSize / 2 + 16;

  // Flip animation — scale pulse + text crossfade (400ms)
  const fadeDuration = 400;
  const fadeElapsed = model.profileVinylFlipMs > 0 ? nowMs() - model.profileVinylFlipMs : fadeDuration;
  const animating = fadeElapsed < fadeDuration;
  const fadeT = Math.min(1, fadeElapsed / fadeDuration);
  const eased = fadeT * fadeT * (3 - 2 * fadeT); // smoothstep
  const showBack = model.profileVinylFlipped;
  // Scale dips to 0.85 at midpoint then back to 1, only during animation
  const scalePulse = animating ? 1 - 0.15 * Math.sin(fadeT * Math.PI) : 1;
  const frontAlpha = showBack ? 1 - eased : eased;
  const backAlpha = showBack ? eased : 1 - eased;

  // Vinyl — always visible, rotating, with scale pulse during flip
  if (vinylImg != null) {
    ctx.save();
    ctx.translate(centerX, vinylCenterY);
    ctx.scale(scalePulse, scalePulse);
    const vinylAngle = (nowMs() / 16000) * Math.PI * 2;
    ctx.rotate(vinylAngle);
    ctx.drawImage(vinylImg, -vinylSize / 2, -vinylSize / 2, vinylSize, vinylSize);
    ctx.restore();
  }

  // Front text
  if (frontAlpha > 0.1) {
    ctx.save();
    ctx.globalAlpha = frontAlpha;
    ctx.shadowColor = "rgba(0,0,0,0.7)";
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 1;
    const tierColor = leaderboardTierColor(tier);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    ctx.font = `bold 8px ${GAME_FONT}`;
    ctx.fillStyle = tierColor;
    const tierLabel = tier === "Regular"
      ? (rankNum != null ? `RANK #${rankNum}` : "")
      : (rankNum != null ? `${tier.toUpperCase()} #${rankNum}` : tier.toUpperCase());
    if (tierLabel) ctx.fillText(tierLabel, centerX, vinylCenterY - 13);

    ctx.font = `bold 12px ${GAME_FONT}`;
    ctx.fillStyle = "#f6fbff";
    const displayName = (model.username ?? "PLAYER").toUpperCase();
    ctx.fillText(displayName, centerX, vinylCenterY + 1);

    ctx.font = `bold 8px ${GAME_FONT}`;
    ctx.fillStyle = "#f6fbff";
    const prestigeStars = model.prestigeStars > 0
      ? " " + "\u2605".repeat(Math.min(5, model.prestigeStars))
      : "";
    ctx.fillText(`LVL ${model.level}${prestigeStars}`, centerX, vinylCenterY + 13);

    if (hasGames) {
      ctx.font = `bold 6px ${GAME_FONT}`;
      ctx.fillStyle = "#c8d8f0";
      const totalGames = player.rankedWins + player.rankedLosses;
      const winRate = totalGames > 0 ? Math.round((player.rankedWins / totalGames) * 100) : 0;
      ctx.fillText(`${player.rankedWins}W ${player.rankedLosses}L  ${winRate}%`, centerX, vinylCenterY + 24);
    }
    ctx.restore();
  }

  // Back text
  if (backAlpha > 0.1) {
    ctx.save();
    ctx.globalAlpha = backAlpha;
    ctx.shadowColor = "rgba(0,0,0,0.7)";
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 1;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // ── B-SIDE — single highest score ──
    const bests = model.personalBests;
    const modeNames: Record<SoloMode, string> = {
      "classic": "CLASSIC",
      "row-rush": "ROW RUSH",
      "clearout": "CLEAROUT",
      "color-hunt": "COLOR HUNT",
      "turbomaxxing": "TURBO",
    };

    // Find the single highest score across all modes
    let topScore = 0;
    let topMode: SoloMode | null = null;
    let topRank: number | null = null;
    for (const mode of Object.keys(modeNames) as SoloMode[]) {
      const entry = bests[mode];
      if (entry != null && entry.score > topScore) {
        topScore = entry.score;
        topMode = mode;
        topRank = entry.rank;
      }
    }

    if (topMode != null) {
      // Game mode logo centered above score
      const logo = soloSelectorLogoImages[topMode];
      if (logo) {
        const logoH = 36;
        const aspect = logo.naturalWidth / (logo.naturalHeight || 1);
        const logoW = logoH * aspect;
        ctx.drawImage(logo, centerX - logoW / 2, vinylCenterY - logoH / 2 - 34, logoW, logoH);
      } else {
        ctx.font = `bold 8px ${GAME_FONT}`;
        ctx.fillStyle = leaderboardTierColor(tier);
        ctx.fillText(modeNames[topMode], centerX, vinylCenterY - 24);
      }

      // Score — large, vertically centered on vinyl
      ctx.font = `bold 12px ${GAME_FONT}`;
      ctx.fillStyle = "#f6fbff";
      ctx.fillText(`${topScore}`, centerX, vinylCenterY);

      // Rank — gold, below score (matches front side spacing)
      if (topRank != null) {
        ctx.font = `bold 8px ${GAME_FONT}`;
        ctx.fillStyle = "#ffd36a";
        ctx.fillText(`RANK #${topRank}`, centerX, vinylCenterY + 14);
      }
    } else {
      ctx.font = `bold 7px ${GAME_FONT}`;
      ctx.fillStyle = "#7a8eaa";
      ctx.fillText("NO SCORES YET", centerX, vinylCenterY + 2);
    }
    ctx.restore();
  }

  // Clickable vinyl rect
  uiRects.profileVinyl = {
    x: centerX - vinylSize / 2,
    y: vinylCenterY - vinylSize / 2,
    w: vinylSize,
    h: vinylSize
  };

  return uiRects;
}
